import { useMutation } from "@apollo/client";
import { useCallback, useEffect, useState } from "react";

import FlashContext from "components/FlashMessages/FlashContext";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import useBlockNavigationModal from "hooks/useBlockNavigationModal";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { Blueprint, BlueprintParseResult, BlueprintState } from "types/generated";
import { INITIAL_TEMPLATE } from "views/Account/Blueprints/constants";
import { showCreateBlueprintDrawer } from "views/Account/Blueprints/CreateBlueprintDrawer";
import { showCreateStackDrawer } from "views/Account/Blueprints/CreateStackDrawer";
import BlueprintsFeatureGateTooltip from "views/Account/Blueprints/FeatureGate/Tooltip";
import { BlueprintActions } from "views/Account/Blueprints/FeatureGate/types";
import { UPDATE_BLUEPRINT } from "views/Account/Blueprints/gql";
import PageInfoHeader from "ds/components/PageInfoHeader";

import BlueprintShareButton from "../components/ShareButton";
import BlueprintEditor from "../Editor";
import { VALIDATE_BLUEPRINT_TEMPLATE } from "../gql";
import { showPublishConfirmationModal } from "../PublishConfirmationModal";
import styles from "./styles.module.css";

type BlueprintTemplateBodyProps = {
  blueprint: Blueprint;
  blueprintSchema: string;
};

const blueprintAnalyticsProps = {
  source: "blueprint-view",
};

const BlueprintTemplateBody = ({ blueprint, blueprintSchema }: BlueprintTemplateBodyProps) => {
  const [editorBody, setEditorBody] = useState<string>(INITIAL_TEMPLATE);
  const { reportSuccess, onError } = useTypedContext(FlashContext);
  const [errors, setErrors] = useState<string[]>([]);

  const [validateTemplate] = useMutation<{
    blueprintParseTemplate: BlueprintParseResult;
  }>(VALIDATE_BLUEPRINT_TEMPLATE);

  const editorHasUnsavedChanges = blueprint.rawTemplate
    ? editorBody !== blueprint.rawTemplate
    : editorBody !== INITIAL_TEMPLATE;

  useBlockNavigationModal({
    title: "Leave confirmation",
    description: "Are you sure you want to leave? You have unsubmitted changes.",
    isNavigationBlocked: editorHasUnsavedChanges,
  });

  const [updateBlueprint, { loading: saveLoading }] = useMutation<{ blueprintUpdate: Blueprint }>(
    UPDATE_BLUEPRINT
  );

  const handleUpdateBlueprint = (publish?: boolean) => {
    if (blueprint) {
      updateBlueprint({
        variables: {
          id: blueprint.id,
          input: {
            name: blueprint.name,
            description: blueprint.description,
            labels: blueprint.labels,
            space: blueprint.space.id,
            state: publish ? BlueprintState.Published : blueprint.state,
            template: editorBody,
          },
        },
      })
        .then(({ data }) => {
          if (data?.blueprintUpdate?.name) {
            reportSuccess({
              message: `Blueprint "${data.blueprintUpdate.name}" is successfully saved`,
            });
          }
        })
        .catch(onError);
    }
  };

  const handleValidateTemplate = useCallback(
    (template: string) => {
      setErrors([]);

      return validateTemplate({
        variables: {
          template,
        },
      })
        .then(({ data }) => {
          setErrors(data?.blueprintParseTemplate?.errors || []);

          return {
            inputs: data?.blueprintParseTemplate?.inputs || [],
            errors: data?.blueprintParseTemplate?.errors || [],
          };
        })
        .catch(onError);
    },
    [onError, validateTemplate]
  );

  const handleSaveTemplate = useCallback(async () => {
    await handleUpdateBlueprint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorBody, blueprint]);

  const handleOpenCreateStackDrawer = () => {
    showCreateStackDrawer({
      item: blueprint,
      onEditMetadata: handleOpenEditDrawer,
    });
  };

  const handleEditorChange = (value?: string) => {
    setEditorBody(value || "");
  };

  const handleOpenEditDrawer = () => {
    showCreateBlueprintDrawer({ blueprint: blueprint });
  };

  useEffect(() => {
    if (blueprint?.rawTemplate) {
      setEditorBody(blueprint.rawTemplate);
      handleValidateTemplate(blueprint.rawTemplate);
    } else {
      handleValidateTemplate(editorBody);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blueprint?.rawTemplate]);

  useTitle(`Template body · ${blueprint?.name}`);

  const isPublished = blueprint.state === BlueprintState.Published;

  const onPublishConfirmation = () => {
    showPublishConfirmationModal({
      onPublish: () => handleUpdateBlueprint(true),
      blueprint,
    });
  };

  return (
    <>
      <Box direction="column" grow="1" fullWidth relative>
        <PageInfoHeader title="Template body">
          {isPublished && blueprint?.id && (
            <BlueprintShareButton
              analyticsProps={blueprintAnalyticsProps}
              blueprintId={blueprint.id}
              size="medium"
              variant="secondary"
            />
          )}
          {isPublished && (
            <BlueprintsFeatureGateTooltip
              action={BlueprintActions.CreateStack}
              on={({ isDisabled, ...props }) => (
                <Button
                  {...props}
                  variant="primary"
                  size="medium"
                  onPress={handleOpenCreateStackDrawer}
                  disabled={isDisabled}
                >
                  Create stack
                </Button>
              )}
            />
          )}

          {!isPublished && (
            <>
              <BlueprintsFeatureGateTooltip
                action={BlueprintActions.Edit}
                blueprint={blueprint}
                on={({ isDisabled, ...props }) => (
                  <Button
                    {...props}
                    variant="secondary"
                    size="medium"
                    onPress={handleSaveTemplate}
                    disabled={saveLoading || isDisabled || !editorHasUnsavedChanges}
                    loading={saveLoading}
                  >
                    Save
                  </Button>
                )}
              />

              <BlueprintsFeatureGateTooltip
                action={BlueprintActions.Publish}
                blueprint={blueprint}
                on={({ isDisabled, ...props }) => (
                  <Button
                    {...props}
                    variant="primary"
                    size="medium"
                    onPress={onPublishConfirmation}
                    disabled={isDisabled || errors.length > 0}
                  >
                    Publish
                  </Button>
                )}
              />
            </>
          )}
        </PageInfoHeader>

        <Box direction="column" grow="1" fullWidth relative className={styles.editorWrapper}>
          <BlueprintEditor
            blueprint={blueprint}
            validate={handleValidateTemplate}
            body={editorBody}
            schema={blueprintSchema}
            onChange={handleEditorChange}
            readOnly={isPublished || blueprint.deleted}
          />
        </Box>
      </Box>
    </>
  );
};

export default BlueprintTemplateBody;
